import React from 'react';
import type { ProductRecord } from '../../lib/productRecords';
import {
  resolveProductRecordDescription,
  resolveProductRecordFeaturedImage,
  resolveProductRecordModelPath,
  resolveProductRecordTitle,
} from '../../lib/productRecords';
import { toWebpVariantUrl } from '../../lib/optimizedImages';
import {
  normalizeGalleryBlockContent,
  resolveProductGalleryProducts,
} from '../../lib/productGallery';

type ProductGalleryBlockSurfaceProps = {
  content?: Record<string, unknown>;
  style?: Record<string, unknown>;
  products: ProductRecord[];
  selectedProductIds: Set<number>;
  moveMode: boolean;
  selectionOverlay: React.ReactNode;
  onSelectProduct: (product: ProductRecord) => void;
  onViewModel: (product: ProductRecord) => void;
};

type ProductGalleryImageProps = {
  src: string;
  alt: string;
  fit: 'cover' | 'contain';
  zoomOnHover: boolean;
};

const GRID_CARD_MIN_WIDTH_PX = 260;
const FEATURED_GRID_CARD_MIN_WIDTH_PX = 220;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function hexToRgba(color: string, alpha: number) {
  const normalized = color.replace('#', '').trim();
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((entry) => `${entry}${entry}`)
          .join('')
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return `rgba(17, 17, 17, ${alpha})`;
  }
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

const ProductGalleryImage: React.FC<ProductGalleryImageProps> = ({
  src,
  alt,
  fit,
  zoomOnHover,
}) => {
  const originalSrc = src.trim();
  const optimizedSrc = toWebpVariantUrl(originalSrc) ?? '';
  const [currentSrc, setCurrentSrc] = React.useState(optimizedSrc || originalSrc);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setCurrentSrc(optimizedSrc || originalSrc);
    setFailed(false);
  }, [optimizedSrc, originalSrc]);

  if (!originalSrc || failed) {
    return (
      <div className="flex h-full w-full items-center justify-center text-xs text-stone-400">
        No image
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={`h-full w-full transition duration-300 ${
        fit === 'contain' ? 'object-contain p-4' : 'object-cover'
      } ${zoomOnHover ? 'group-hover:scale-[1.03]' : ''}`}
      loading="lazy"
      draggable={false}
      onError={() => {
        if (currentSrc !== originalSrc) {
          setCurrentSrc(originalSrc);
          return;
        }
        setFailed(true);
      }}
    />
  );
};

const ProductGalleryBlockSurface: React.FC<ProductGalleryBlockSurfaceProps> = ({
  content,
  style,
  products,
  selectedProductIds,
  moveMode,
  selectionOverlay,
  onSelectProduct,
  onViewModel,
}) => {
  const settings = React.useMemo(() => normalizeGalleryBlockContent(content), [content]);
  const visibleProducts = React.useMemo(
    () => resolveProductGalleryProducts(products, settings),
    [products, settings]
  );
  const cardRadius = clamp(Number(style?.borderRadius) || 18, 0, 40);
  const hoverEffect = typeof style?.hoverEffect === 'string' ? style.hoverEffect : 'none';
  const hoverOverlayColour =
    typeof style?.hoverOverlayColour === 'string' ? style.hoverOverlayColour : '#111111';
  const hoverOverlayOpacity = clamp(Number(style?.hoverOverlayOpacity) || 0.24, 0, 1);
  const imageRatio = typeof style?.imageRatio === 'string' ? style.imageRatio : 'landscape';
  const imageFit = style?.imageFit === 'contain' ? 'contain' : 'cover';
  const cardTone = typeof style?.cardTone === 'string' ? style.cardTone : 'soft';
  const contentDensity = typeof style?.contentDensity === 'string' ? style.contentDensity : 'comfortable';
  const textAlign = style?.textAlign === 'center' ? 'center' : 'left';

  const imageRatioClass =
    imageRatio === 'portrait'
      ? 'aspect-[4/5]'
      : imageRatio === 'square'
        ? 'aspect-square'
        : imageRatio === 'cinema'
          ? 'aspect-[16/9]'
          : 'aspect-[4/3]';
  const cardToneClass =
    cardTone === 'elevated'
      ? 'border-stone-100 bg-white shadow-md'
      : cardTone === 'outlined'
        ? 'border-stone-300 bg-white shadow-none'
        : 'border-stone-200 bg-white shadow-sm';
  const contentDensityClass =
    contentDensity === 'compact'
      ? 'gap-2 p-3'
      : contentDensity === 'spacious'
        ? 'gap-4 p-5'
        : 'gap-3 p-4';
  const textAlignClass = textAlign === 'center' ? 'text-center' : 'text-left';
  const buttonJustifyClass = textAlign === 'center' ? 'justify-center' : 'justify-start';

  const renderCard = React.useCallback(
    (product: ProductRecord, options?: { featured?: boolean; stretch?: boolean }) => {
      const featured = options?.featured === true;
      const stretch = options?.stretch === true;
      const title = resolveProductRecordTitle(product) || product.name || 'Product';
      const description = resolveProductRecordDescription(product);
      const image = resolveProductRecordFeaturedImage(product);
      const modelPath = resolveProductRecordModelPath(product);
      const isSelected = selectedProductIds.has(product.id);
      const showHoverOverlay = hoverEffect === 'overlay';

      return (
        <article
          key={product.id}
          className={`group relative flex flex-col overflow-hidden border transition ${
            stretch ? 'h-full' : ''
          } ${cardToneClass} ${
            hoverEffect === 'zoom' || cardTone === 'elevated'
              ? 'hover:-translate-y-0.5 hover:shadow-md'
              : 'hover:shadow-md'
          }`}
          style={{ borderRadius: `${cardRadius}px` }}
        >
          <div className={`relative shrink-0 overflow-hidden bg-stone-100 ${featured ? 'aspect-[5/4]' : imageRatioClass}`}>
            <ProductGalleryImage
              src={image}
              alt={title}
              fit={imageFit}
              zoomOnHover={hoverEffect === 'zoom'}
            />
            {showHoverOverlay ? (
              <div
                className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100"
                style={{ backgroundColor: hexToRgba(hoverOverlayColour, hoverOverlayOpacity) }}
              />
            ) : null}
          </div>

          <div className={`flex flex-1 flex-col ${contentDensityClass}`}>
            {settings.showTitle ? (
              <div className={`text-sm font-semibold text-stone-900 ${textAlignClass}`}>{title}</div>
            ) : null}
            {settings.showDescription ? (
              <div className={`line-clamp-2 text-sm text-stone-600 ${textAlignClass}`}>
                {description || 'No description'}
              </div>
            ) : null}
            {settings.showPrice ? (
              <div className={`text-sm font-medium text-stone-900 ${textAlignClass}`}>
                {typeof product.priceAmount === 'number' && Number.isFinite(product.priceAmount)
                  ? `${product.currencyCode || 'GBP'} ${product.priceAmount.toFixed(2)}`
                  : 'Price on request'}
              </div>
            ) : null}
            <div className={`mt-auto flex flex-wrap gap-2 ${buttonJustifyClass}`}>
              {settings.showSelectButton ? (
                <button
                  type="button"
                  data-builder-allow-interaction
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelectProduct(product);
                  }}
                  className={`inline-flex min-w-[140px] flex-1 items-center justify-center rounded-full px-3 py-2 text-center text-sm font-medium transition ${
                    isSelected
                      ? 'bg-stone-900 text-white'
                      : 'border border-stone-300 bg-white text-stone-900 hover:bg-stone-50'
                  }`}
                  style={{ opacity: moveMode ? 0.7 : 1 }}
                >
                  {isSelected ? 'Selected' : 'Select'}
                </button>
              ) : null}
              {settings.showView3DButton ? (
                <button
                  type="button"
                  data-builder-allow-interaction
                  disabled={!modelPath}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!modelPath) return;
                    onViewModel(product);
                  }}
                  className="inline-flex min-w-[140px] flex-1 items-center justify-center rounded-full border border-stone-300 bg-white px-3 py-2 text-center text-sm font-medium text-stone-900 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  View in 3D
                </button>
              ) : null}
            </div>
          </div>
        </article>
      );
    },
    [
      buttonJustifyClass,
      cardRadius,
      cardTone,
      cardToneClass,
      contentDensityClass,
      hoverEffect,
      hoverOverlayColour,
      hoverOverlayOpacity,
      imageFit,
      imageRatioClass,
      moveMode,
      onSelectProduct,
      onViewModel,
      selectedProductIds,
      settings.showDescription,
      settings.showPrice,
      settings.showSelectButton,
      settings.showTitle,
      settings.showView3DButton,
      textAlignClass,
    ]
  );

  const layoutVariant = String(settings.layoutVariant).toLowerCase();

  return (
    <div className="relative h-full w-full overflow-hidden rounded-lg border border-stone-200 bg-stone-50">
      {visibleProducts.length === 0 ? (
        <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-stone-500">
          No products match this gallery source yet.
        </div>
      ) : layoutVariant === 'product-gallery' ? (
        <div className="grid h-full grid-rows-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-3 p-3">
          <div className="min-h-0">{renderCard(visibleProducts[0], { featured: true, stretch: true })}</div>
          <div
            className="grid min-h-0 overflow-auto gap-3 pr-1"
            style={{
              gridTemplateColumns: `repeat(auto-fit, minmax(${FEATURED_GRID_CARD_MIN_WIDTH_PX}px, 1fr))`,
            }}
          >
            {visibleProducts.slice(1).map((product) => renderCard(product))}
          </div>
        </div>
      ) : layoutVariant === 'hero-carousel' ? (
        <div className={`h-full overflow-x-auto overflow-y-hidden p-3 ${moveMode ? 'pointer-events-none' : ''}`}>
          <div className="flex h-full gap-3">
            {visibleProducts.map((product) => (
              <div key={product.id} className="min-w-[280px] flex-[0_0_280px]">
                {renderCard(product, { stretch: true })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div
          className="grid h-full overflow-auto p-3"
          style={{
            gap: `${settings.spacing}px`,
            gridTemplateColumns: `repeat(auto-fit, minmax(${GRID_CARD_MIN_WIDTH_PX}px, 1fr))`,
          }}
        >
          {visibleProducts.map((product) => renderCard(product))}
        </div>
      )}
      {selectionOverlay}
    </div>
  );
};

export default ProductGalleryBlockSurface;
